<template>
  <div class="container py-4">
    <div class="page-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">Stock receipts</h4>

        <icon-button
          :func="() => openReceive()"
          :icon="'plus'"
          :title="'Receive stock'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Receive stock'"
        />
      </div>

      <b-table
        :items="receipts"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
      >
        <template #cell(total)="data">{{ formatMoney(data.value) }}</template>
        <template #cell(receivedAt)="data">{{
          formatDate(data.value)
        }}</template>
        <template #cell(actions)="data">
          <icon-button
            :func="() => viewReceipt(data.item.id)"
            :icon="'eye'"
            :title="'View receipt'"
            :classes="'akkurate-orange btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="showReceive"
      title="Receive stock"
      size="lg"
      @ok="submitReceipt"
    >
      <b-form-group label="Supplier">
        <b-form-select
          v-model="form.supplierId"
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          :options="supplierOptions"
          required
        />
      </b-form-group>

      <h6 class="mt-3">Line items</h6>
      <div
        v-for="(line, idx) in form.items"
        :key="idx"
        class="d-flex gap-2 mb-2 align-items-end flex-wrap"
      >
        <b-form-group label="Product" class="flex-grow-1 mb-0">
          <b-form-select
            class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
            v-model="line.productId"
            :options="productOptions"
          />
        </b-form-group>
        <b-form-group label="Qty" class="mb-0" style="width: 90px">
          <b-form-input
            class="form-control"
            v-model="line.quantity"
            type="number"
            min="1"
          />
        </b-form-group>
        <b-form-group label="Unit cost" class="mb-0" style="width: 110px">
          <b-form-input
            class="form-control"
            v-model="line.unitCost"
            type="number"
            min="0"
            step="0.01"
          />
        </b-form-group>

        <icon-button
          v-if="form.items.length > 1"
          :func="() => form.items.splice(idx, 1)"
          :icon="'times'"
          :title="'Remove'"
          :classes="'akkurate-danger-bg btn-sm'"
          :btnName="''"
        />
      </div>

      <icon-button
        :func="() => addLine()"
        :icon="'plus'"
        :title="'Add line'"
        :classes="'akkurate-green-btn btn-size2'"
        :btnName="'Add line'"
      />

      <b-form-group label="Tax" class="mt-3">
        <b-form-input
          class="form-control"
          v-model="form.tax"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="form.notes" rows="2" />
      </b-form-group>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />

        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Save'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save'"
        />
      </template>
    </b-modal>

    <b-modal
      v-model="showDetail"
      title="Receipt detail"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <div v-if="detail">
        <p>
          <strong>{{ detail.receiptNumber }}</strong> —
          {{ detail.supplierName }}
        </p>
        <p class="small text-muted">{{ formatDate(detail.receivedAt) }}</p>
        <b-table :items="detail.items" :fields="detailFields" small responsive>
          <template #cell(unitCost)="data">{{
            formatMoney(data.value)
          }}</template>
          <template #cell(total)="data">{{ formatMoney(data.value) }}</template>
        </b-table>
        <p class="mb-0">
          <strong>Total:</strong> {{ formatMoney(detail.total) }}
        </p>
      </div>

      <template #modal-footer="{ ok }">
        <icon-button
          :func="() => ok()"
          :icon="'times'"
          :title="'Close'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Close'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";

export default {
  name: "StockReceipts",
  data() {
    return {
      receipts: [],
      suppliers: [],
      products: [],
      loading: false,
      showReceive: false,
      showDetail: false,
      detail: null,
      form: {
        supplierId: "",
        items: [{ productId: "", quantity: 1, unitCost: 0 }],
        tax: 0,
        notes: "",
      },
      fields: [
        { key: "receiptNumber", label: "Receipt #" },
        { key: "supplierName", label: "Supplier" },
        { key: "total", label: "Total" },
        { key: "receivedAt", label: "Received" },
        { key: "receivedByName", label: "By" },
        { key: "actions", label: "Actions" },
      ],
      detailFields: [
        { key: "productSku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "quantity", label: "Qty" },
        { key: "unitCost", label: "Cost" },
        { key: "total", label: "Line total" },
      ],
    };
  },
  components: {
    IconButton,
  },
  computed: {
    ...mapGetters(["selectedStoreId"]),
    supplierOptions() {
      return [
        { value: "", text: "Select supplier" },
        ...this.suppliers.map((s) => ({ value: s.id, text: s.name })),
      ];
    },
    productOptions() {
      return [
        { value: "", text: "Select product" },
        ...this.products.map((p) => ({
          value: p.id,
          text: `${p.sku} — ${p.name}`,
        })),
      ];
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString();
    },
    async load() {
      if (!this.selectedStoreId) return;
      this.loading = true;
      try {
        this.receipts = await ApiService.get("stock-receipts", {
          storeId: this.selectedStoreId,
        });
      } finally {
        this.loading = false;
      }
    },
    async openReceive() {
      const [suppliers, products] = await Promise.all([
        ApiService.get("suppliers"),
        ApiService.get("products", {
          storeId: this.selectedStoreId,
          sellableOnly: true,
        }),
      ]);
      this.suppliers = suppliers;
      this.products = products;
      this.form = {
        supplierId: "",
        items: [{ productId: "", quantity: 1, unitCost: 0 }],
        tax: 0,
        notes: "",
      };
      this.showReceive = true;
    },
    addLine() {
      this.form.items.push({ productId: "", quantity: 1, unitCost: 0 });
    },
    async submitReceipt(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        await ApiService.post("stock-receipts", {
          supplierId: this.form.supplierId,
          storeId: this.selectedStoreId,
          tax: Number(this.form.tax) || 0,
          notes: this.form.notes || null,
          items: this.form.items.map((l) => ({
            productId: l.productId,
            quantity: Number(l.quantity),
            unitCost: Number(l.unitCost),
          })),
        });
        this.showReceive = false;
        await this.load();
        toast.fire({
          title: "Stock received",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Receive failed",
          icon: "danger",
        });
      }
    },
    async viewReceipt(id) {
      this.detail = await ApiService.get(`stock-receipts/${id}`);
      this.showDetail = true;
    },
  },
};
</script>
